import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import axios from "config/axios";
import { toast } from "react-toastify";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const CourseMap = () => {
  const [courseMaps, setCourseMaps] = useState([]);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedMap, setSelectedMap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Fetch all course maps
  const fetchCourseMaps = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/courseMapSection/course-map-section`);
      setCourseMaps(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch course maps");
    }
  };

  useEffect(() => {
    fetchCourseMaps();
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file || !title.trim()) {
      toast.error("Please select a file and enter a title");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", title);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/courseMapSection/upload-course-map-section`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Course map uploaded");
        setMapModalOpen(false);
        setFile(null);
        setTitle("");
        await fetchCourseMaps();
      }
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("Failed to upload course map");
    }
  };

  const handleView = (map) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/courseMapSection/${map._id}/course-map-section`;
    window.open(url, "_blank");
  };

  const handleDelete = async (map) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/courseMapSection/${map._id}/delete-course-map-section`
      );
      if (response.status === 200) {
        toast.success("Course map deleted successfully");
        setCourseMaps((prev) => prev.filter((item) => item._id !== map._id));
        setConfirmDelete(false);
        setSelectedMap(null);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete course map");
    }
  };

  const handleEdit = async () => {
    if (!selectedMap) {
      toast.error("No course map selected for update");
      return;
    }
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", title);
      if (file) formData.append("file", file);

      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/courseMapSection/${selectedMap._id}/update-course-map-section`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.status === 200) {
        toast.success("Course map updated successfully");
        setMapModalOpen(false);
        setFile(null);
        setTitle("");
        setSelectedMap(null);
        setIsEditing(false);
        await fetchCourseMaps();
      }
    } catch (err) {
      console.error("Update failed:", err);
      toast.error("Failed to update course map");
    }
  };

  return (
    <div className="bg-white flex flex-col justify-center items-center">
      {/* Upload Section */}
      <div className="w-4/5 mt-7">
        <h2 className="text-2xl text-center font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight mb-3">
          Create Course Map
        </h2>
        <div className="rounded overflow-hidden shadow-lg">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="font-bold text-lg sm:text-xl mb-2">Course Map</div>
            <button
              className="bg-blue-500 hover:bg-blue-400 text-white font-bold py-2 px-4 border-b-4 border-blue-700 hover:border-blue-500 rounded"
              onClick={() => {
                setIsEditing(false);
                setSelectedMap(null);
                setTitle("");
                setFile(null);
                setMapModalOpen(true);
              }}
            >
              Upload
            </button>
          </div>
        </div>
      </div>

      {/* View Section */}
      <div className="w-4/5 mt-10">
        <h2 className="text-2xl text-center font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight mb-3">
          View Course Map
        </h2>
        <div className="rounded overflow-hidden shadow-lg">
          <div className="px-6 py-4">
            {courseMaps && courseMaps.length > 0 ? (
              courseMaps.map((map) => (
                <div
                  key={map._id}
                  className="flex justify-between items-center mt-2 p-3 border rounded shadow-sm"
                >
                  {/* Left: Title */}
                  <span className="font-semibold">{map.title}</span>

                  {/* Right: Buttons */}
                  <div className="flex gap-2">
                    <button
                      className="bg-green-500 hover:bg-green-400 text-white font-bold py-2 px-4 border-b-4 border-green-700 hover:border-green-500 rounded"
                      onClick={() => handleView(map)}
                    >
                      View
                    </button>

                    <button
                      className="bg-yellow-500 hover:bg-yellow-400 text-white font-bold py-2 px-4 border-b-4 border-yellow-700 hover:border-yellow-500 rounded"
                      onClick={() => {
                        setSelectedMap(map);
                        setTitle(map.title);
                        setIsEditing(true);
                        setMapModalOpen(true);
                      }}
                    >
                      Edit
                    </button>

                    {/* Delete Confirmation */}
                    {selectedMap?._id === map._id && confirmDelete ? (
                      <>
                        <button
                          className="bg-red-500 hover:bg-red-400 text-white font-bold py-2 px-4 border-b-4 border-red-700 hover:border-red-500 rounded"
                          onClick={() => handleDelete(map)}
                        >
                          Confirm
                        </button>
                        <button
                          className="bg-gray-500 hover:bg-gray-400 text-white font-bold py-2 px-4 border-b-4 border-gray-700 hover:border-gray-500 rounded"
                          onClick={() => setConfirmDelete(false)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        className="bg-red-500 hover:bg-red-400 text-white font-bold py-2 px-4 border-b-4 border-red-700 hover:border-red-500 rounded"
                        onClick={() => {
                          setSelectedMap(map);
                          setConfirmDelete(true);
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 text-lg">
                  No course maps available
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className="rounded-lg"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            {isEditing ? "Edit Course Map" : "Upload Course Map"}
          </Typography>

          <TextField
            className="w-full mt-3"
            label="Title"
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <TextField
            className="w-full mt-3"
            type="file"
            onChange={handleFileChange}
          />

          <div className="flex w-full justify-end items-center mt-3">
            <Button
              style={{ marginRight: "5px" }}
              variant="contained"
              onClick={isEditing ? handleEdit : handleUpload}
            >
              {isEditing ? "Update" : "Upload"}
            </Button>
            <Button
              style={{ marginLeft: "5px" }}
              variant="contained"
              onClick={() => setMapModalOpen(false)}
            >
              Close
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default CourseMap;
